import axios from "axios";
import * as config from "@/config";

const api = config.apiUrl;

/**
 * Updates order detail data for a specific order and detail item
 * @param {string|number} orderId - The order ID
 * @param {string|number} detailId - The order detail ID
 * @param {number} aantal - The quantity
 * @returns {Promise<Object>} - API response data
 */
export const updateOrderDetail = async (orderId, detailId, aantal) => {
    try {
        // Validate required parameters
        if (!orderId || !detailId || aantal === null || aantal === undefined) {
            throw new Error('Missing required parameters: orderId, detailId, and aantal are required');
        }

        // Ensure aantal is a valid number
        const parsedAantal = parseFloat(aantal);
        if (isNaN(parsedAantal)) {
            throw new Error('aantal must be a valid number');
        }

        const putObject = {
            aantal: parsedAantal
        };

        console.log(`Updating order detail for order ${orderId}, detailId ${detailId}:`, putObject);

        const response = await axios.put(
            `${api}/order/${orderId}/details/${detailId}`, 
            putObject
        );

        console.log('Order detail update response:', response.data);
        return response.data;

    } catch (error) {
        console.error('Error updating order detail:', error);
        
        // Re-throw with more context
        if (error.response) {
            // Server responded with error status
            const serverError = new Error(`Server error: ${error.response.status} - ${error.response.statusText}`);
            serverError.response = error.response;
            throw serverError;
        } else if (error.request) {
            // Request was made but no response received
            throw new Error('Network error: No response received from server');
        } else {
            // Something else happened
            throw error;
        }
    }
};

/**
 * Batch update multiple order detail items
 * @param {string|number} orderId - The order ID
 * @param {Array} detailUpdates - Array of {detailId, aantal} objects
 * @returns {Promise<Array>} - Array of API responses
 */
export const updateMultipleOrderDetails = async (orderId, detailUpdates) => {
    try {
        if (!orderId || !Array.isArray(detailUpdates) || detailUpdates.length === 0) {
            throw new Error('Missing required parameters: orderId and detailUpdates array are required');
        }

        console.log(`Batch updating ${detailUpdates.length} order detail items for order ${orderId}`);

        // Execute all updates in parallel
        const updatePromises = detailUpdates.map(update => 
            updateOrderDetail(orderId, update.detailId, update.aantal)
        );

        const results = await Promise.allSettled(updatePromises);
        
        // Check for any failures
        const failures = results.filter(result => result.status === 'rejected');
        if (failures.length > 0) {
            console.warn(`${failures.length} out of ${results.length} order detail updates failed`);
            failures.forEach((failure, index) => {
                console.error(`Update ${index} failed:`, failure.reason);
            });
        }

        return results;

    } catch (error) {
        console.error('Error in batch order detail update:', error);
        throw error;
    }
};

/**
 * Validates order detail data before sending to API
 * @param {Object} orderDetailItem - The order detail item to validate (expects opdrachtDetailId, aantal)
 * @returns {Object} - Validation result {isValid: boolean, errors: Array}
 */
export const validateOrderDetailData = (orderDetailItem) => {
    const errors = [];

    if (!orderDetailItem.opdrachtDetailId && !orderDetailItem.id && !orderDetailItem.detailId) {
        errors.push('Missing order detail ID');
    }

    if (orderDetailItem.aantal === null || orderDetailItem.aantal === undefined || orderDetailItem.aantal === '') {
        errors.push('aantal is required');
    } else {
        const parsedAantal = parseFloat(orderDetailItem.aantal);
        if (isNaN(parsedAantal)) {
            errors.push('aantal must be a valid number');
        } else if (parsedAantal < 0) {
            errors.push('aantal cannot be negative');
        }
    }

    return {
        isValid: errors.length === 0,
        errors
    };
};
